import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  careFetchMe,
  careLoginWithPassword,
  getCareToken,
  setCareToken,
  userHasTeleassistanceRole,
} from '../lib/careApi';

const CareAuthContext = createContext(null);

export function CareAuthProvider({ children }) {
  const [token, setTokenState] = useState(() => getCareToken());
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(Boolean(getCareToken()));

  const logout = useCallback(() => {
    setCareToken('');
    setTokenState('');
    setUser(null);
  }, []);

  const refreshUser = useCallback(async (nextToken) => {
    const t = nextToken || token;
    if (!t) {
      setUser(null);
      setLoading(false);
      return null;
    }
    const me = await careFetchMe(t);
    if (!userHasTeleassistanceRole(me)) {
      throw new Error('Accès plateau Care refusé.');
    }
    setUser(me);
    return me;
  }, [token]);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      setUser(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const me = await careFetchMe(token);
        if (cancelled) return;
        if (!userHasTeleassistanceRole(me)) {
          logout();
          return;
        }
        setUser(me);
      } catch {
        if (!cancelled) logout();
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token, logout]);

  const login = useCallback(async (password) => {
    const res = await careLoginWithPassword(password);
    const nextToken = res.access_token;
    if (!nextToken) throw new Error('Connexion impossible.');
    setCareToken(nextToken);
    setTokenState(nextToken);
    const me = await refreshUser(nextToken);
    return me;
  }, [refreshUser]);

  const value = useMemo(() => ({
    token,
    user,
    loading,
    login,
    logout,
    isAuthenticated: Boolean(token && user),
  }), [token, user, loading, login, logout]);

  return <CareAuthContext.Provider value={value}>{children}</CareAuthContext.Provider>;
}

export function useCareAuth() {
  const ctx = useContext(CareAuthContext);
  if (!ctx) throw new Error('useCareAuth doit être utilisé dans CareAuthProvider');
  return ctx;
}
